import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const shouldAllowDropdownToClose = (e) => {
  if (!e.target.classList || !e.target.classList.length) {
    return false;
  }
  return [...new Set(e.target.classList)].some((cssClass) => cssClass.indexOf('close-button') !== -1);
};

const getDropdownItemKey = (item, keyProp) => {
  if (typeof item === 'string') {
    return item;
  }
  return item[keyProp];
};

/**
 * @param {object} props
 * @param {boolean} [props.dropdownMenuShouldHaveWidthOfButton]
 * @param {T[]|string[]} props.items
 * @param {string} [props.itemKeyName]
 * @param {(arg: T[]|string[]) => void} [props.onUpdate]
 * @param {(arg: T|string, isChecked: boolean) => void} [props.onItemToggled]
 * @param {string|React.ReactNode} props.toggleLabel
 * @param {(sectionTitle: string) => React.ReactNode} [props.renderSectionLabel]
 * @param {(arg: T|string, isChecked: boolean) => React.ReactNode} [props.renderItemLabel]
 * @param {boolean} [props.disabled]
 * @param {T[]|string[]} [props.selectedItems]
 * @param {boolean} [props.shouldUpdateOnClose]
 * @param {boolean} [props.isManualApply] - when true, shows Clear/Apply buttons and requires user to click Apply to commit changes
 * @param {string} [props.className]
 * @param {string} [props.togglerClassName]
 * @param {React.ReactNode} [props.children]
 * @returns {JSX.Element}
 */
const MultiCheckDropdown = ({
  dropdownMenuShouldHaveWidthOfButton = false,
  items,
  itemKeyName,
  onUpdate = noop,
  onItemToggled = noop,
  toggleLabel,
  renderSectionLabel = (title) => title,
  renderItemLabel = (label) => label,
  disabled = false,
  selectedItems = emptyArray,
  shouldUpdateOnClose = false,
  isManualApply = false,
  togglerClassName = '',
  className = '',
  children = null,
}) => {
  const [locallySelectedItemIds, setLocallySelectedItemIds] = useAsyncSafeState(selectedItems);

  const onToggleItem = (itemId, checked) => {
    if (checked) {
      setLocallySelectedItemIds((arr) => [...arr, itemId]);
    } else {
      setLocallySelectedItemIds((arr) => arr.filter((id) => id !== itemId));
    }
    onItemToggled(itemId, checked);
  };

  const onClearSelections = useCallback(() => {
    setLocallySelectedItemIds([]);
  }, [setLocallySelectedItemIds]);

  const onHandleOpen = useCallback(() => {
    setLocallySelectedItemIds(selectedItems);
  }, [selectedItems, setLocallySelectedItemIds]);

  const onHandleClose = useCallback(() => {
    if (shouldUpdateOnClose) {
      onUpdate(locallySelectedItemIds);
    }
  }, [locallySelectedItemIds, shouldUpdateOnClose, onUpdate]);

  const onHandleApply = useCallback(() => {
    const shouldApplyOnlyOnApply = !shouldUpdateOnClose;
    if (shouldApplyOnlyOnApply) {
      onUpdate(locallySelectedItemIds);
    }
  }, [locallySelectedItemIds, shouldUpdateOnClose, onUpdate]);

  const renderItems = (itemIds) => {
    return itemIds.map((itemId) => {
      const isChecked = locallySelectedItemIds.indexOf(itemId) !== -1;
      return (
        <DropdownMenuItem key={getDropdownItemKey(itemId, itemKeyName)} onClick={() => onToggleItem(itemId, !isChecked)}>
          <Checkbox checked={isChecked} className={style.checkbox} onChange={noop} />
          {renderItemLabel(itemId, isChecked)}
        </DropdownMenuItem>
      );
    });
  };
  return (
    <>
      <DropdownMenu
        className={classnames(style['dropdown-menu'], className)}
        closeWhenChildrenClicked={false}
        containerAllowedMeToClose={shouldAllowDropdownToClose}
        disabled={disabled}
        dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
        onClose={onHandleClose}
        onOpen={onHandleOpen}
        variant='form'
      >
        <DropdownMenuToggler className={classnames(style.toggler, togglerClassName)}>{toggleLabel}</DropdownMenuToggler>
        <div className={style['dropdown-items']}>
          {!Array.isArray(items) &&
            Object.entries(items).map(([sectionTitle, itemIds]) => (
              <div key={sectionTitle} className={style.section}>
                <div className={style['section-header']}>{renderSectionLabel(sectionTitle)}</div>
                {renderItems(itemIds)}
              </div>
            ))}
          {Array.isArray(items) && renderItems(items)}
        </div>
        <div className={style['dropdown-footer']}>
          <FormattedMessage
            defaultMessage='{count} Selected'
            description='Multi check dropdown menu selected count'
            id='q91Sid'
            values={{
              count: locallySelectedItemIds.length,
            }}
          />
          <div>
            {isManualApply ? (
              <>
                <Button disabled={!(locallySelectedItemIds.length > 0)} onClick={onClearSelections} theme='link'>
                  <FormattedMessage defaultMessage='Clear' description='Multi check dropdown menu clear button' id='SM3dc6' />
                </Button>
                <Button className={classnames('close-button', style['close-button'])} onClick={onHandleApply} theme='link'>
                  <FormattedMessage defaultMessage='Apply' description='Multi check dropdown menu apply button' id='8WiYnZ' />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </DropdownMenu>
      {children}
    </>
  );
};

export default MultiCheckDropdown;
